import { REPORTS } from "../reports/registry";

// Addresses. Pages are known inside the app by a short internal path ("vouchers/new/sales", "masters/groups",
// "reports/r/trial_balance"); the browser shows a readable address for each ("/sales/invoices/new",
// "/accounting/chart-of-accounts", "/reports/trial-balance"). The company isn't in the address: it is the one chosen
// in this tab (shell/company.ts). toUrl and fromUrl turn one into the other; anything with a query keeps it.

const NEW_DOC: Record<string, string> = {
  sales: "/sales/invoices/new", credit_note: "/sales/credit-notes/new", receipt: "/sales/receipts/new",
  purchase: "/purchases/bills/new", debit_note: "/purchases/debit-notes/new", payment: "/purchases/payments/new",
  contra: "/banking/transfers/new", journal: "/accounting/journal/new",
};
const FIXED: Record<string, string> = {
  "masters/groups": "/accounting/chart-of-accounts",
  "masters/ledgers": "/accounting/accounts",
  "masters/openings": "/accounting/opening-balances",
  "masters/currencies": "/accounting/currencies",
  "masters/revaluation": "/accounting/revaluation",
  "vouchers/daybook": "/accounting/register",
  "documents/sales": "/sales/invoices",
  "documents/receipt": "/sales/payments-received",
  "documents/credit_note": "/sales/credit-notes",
  "documents/purchase": "/purchases/bills",
  "documents/payment": "/purchases/payments-made",
  "documents/debit_note": "/purchases/debit-notes",
  "company/settings": "/settings/company",
  "company/users": "/settings/people",
  "company/documents": "/settings/invoice-look",
  "company/import": "/settings/import",
  "gst/gstr1": "/compliance/gstr-1",
  "gst/gstr2b": "/compliance/gstr-2b",
  "gst/gstr3b": "/compliance/gstr-3b",
  "gst/composition": "/compliance/composition",
  "gst/annual": "/compliance/gstr-9",
  "gst/challans": "/compliance/gst-challans",
  "gst/isd": "/compliance/isd",
  "gst/iff": "/compliance/iff",
  "gst/gstr1a": "/compliance/gstr-1a",
  "gst/rcm": "/compliance/reverse-charge-documents",
  "reports/scheduled": "/reports/by-email",
  "assets/register": "/accounting/fixed-assets",
  "settings/roles": "/settings/roles",
  "settings/security": "/settings/security",
  "accounting/approvals": "/accounting/approvals",
  "gst/ecommerce": "/compliance/e-commerce",
  "gst/ewaybills": "/compliance/e-way-bills",
  "gst/ewaybill-limits": "/compliance/e-way-bill-limits",
  "tds/challans": "/compliance/tds-deposits",
  "tds/statements": "/compliance/tds-statements",
  "tds/certificates": "/compliance/tds-certificates",
  "tds/form168": "/compliance/form-168",
  "banking/accounts": "/banking/accounts",
  "accounting/budgets": "/accounting/budgets",
  "accounting/cost-centres": "/accounting/cost-centres",
  "accounting/employees": "/accounting/employees",
  "accounting/students": "/accounting/students",
  "reports/ask": "/reports/ask",
  "sales/projects": "/sales/projects",
  "sales/timesheet": "/sales/timesheet",
  "settings/api": "/settings/api",
  "settings/payroll": "/settings/payroll",
  "settings/edit-log": "/settings/edit-log",
  "sales/schemes": "/sales/schemes",
  "purchases/claims": "/purchases/expense-claims",
  "reports/cash-forecast": "/reports/cash-forecast",
  "banking/reconcile": "/banking/reconcile",
  "inventory/items": "/inventory/items",
  "inventory/setup": "/inventory/setup",
  "inventory/stock-entry": "/inventory/stock-adjustment",
  "parties/customers": "/sales/customers",
  "parties/vendors": "/purchases/vendors",
  "orders/sales": "/sales/orders",
  "orders/sales/new": "/sales/orders/new",
  "orders/purchase": "/purchases/orders",
  "orders/purchase/new": "/purchases/orders/new",
  "orders/quote": "/sales/quotes",
  "orders/quote/new": "/sales/quotes/new",
  "orders/proforma": "/sales/proformas",
  "orders/proforma/new": "/sales/proformas/new",
  "orders/challan": "/sales/challans",
  "orders/challan/new": "/sales/challans/new",
  "recurring/sales": "/sales/recurring",
  "recurring/sales/new": "/sales/recurring/new",
  "recurring/purchase": "/purchases/recurring",
  "recurring/purchase/new": "/purchases/recurring/new",
};
// where a report's area sits in the menu
const REPORT_AREA: Record<string, string> = { gst: "compliance", tds: "compliance", msme: "compliance", reports: "reports", banking: "banking", inventory: "inventory" };
export const SECTION_IDS = ["sales", "purchases", "banking", "inventory", "accounting", "compliance", "reports", "settings"];

const split = (p: string) => { const i = p.indexOf("?"); return i < 0 ? [p, ""] : [p.slice(0, i), p.slice(i)]; };

export function toUrl(internal: string): string {
  const [raw, q] = split(internal.replace(/^\/+/, ""));
  const p = raw.replace(/\/+$/, "");
  let out: string;
  if (!p) out = "/";
  else if (FIXED[p]) out = FIXED[p];
  else if (SECTION_IDS.includes(p)) out = `/${p}`;
  else {
    let m: RegExpMatchArray | null;
    if ((m = p.match(/^vouchers\/new\/(\w+)$/))) out = NEW_DOC[m[1]] ?? `/accounting/journal/new`;
    else if ((m = p.match(/^vouchers\/v\/([\w-]+)$/))) out = `/transactions/${m[1]}`;
    else if ((m = p.match(/^vouchers\/v\/([\w-]+)\/print$/))) out = `/transactions/${m[1]}/print`;
    else if ((m = p.match(/^masters\/ledgers\/([\w-]+)$/))) out = `/accounting/accounts/${m[1]}`;
    else if ((m = p.match(/^inventory\/items\/([\w-]+)$/))) out = `/inventory/items/${m[1]}`;
    else if ((m = p.match(/^parties\/(customers|vendors)\/([\w-]+)\/statement$/))) out = `/${m[1] === "customers" ? "sales" : "purchases"}/${m[1]}/${m[2]}/statement`;
    else if ((m = p.match(/^orders\/(quote|proforma|challan)\/([\w-]+)(\/print)?$/))) out = `/sales/${m[1]}s/${m[2]}${m[3] ?? ""}`;
    else if ((m = p.match(/^(orders)\/(sales|purchase)\/([\w-]+)\/print$/))) out = `/${m[2] === "sales" ? "sales" : "purchases"}/${m[1]}/${m[3]}/print`;
    else if ((m = p.match(/^parties\/(customers|vendors)\/([\w-]+)$/))) out = `/${m[1] === "customers" ? "sales" : "purchases"}/${m[1]}/${m[2]}`;
    else if ((m = p.match(/^(orders|recurring)\/(sales|purchase)\/([\w-]+)$/))) out = `/${m[2] === "sales" ? "sales" : "purchases"}/${m[1]}/${m[3]}`;
    else if ((m = p.match(/^(\w+)\/r\/(\w+)$/))) {
      const area = REPORT_AREA[m[1]] ?? m[1], slug = m[2].replace(/_/g, "-");
      out = area === "reports" ? `/reports/${slug}` : `/${area}/reports/${slug}`;
    }
    else out = `/${p}`;
  }
  return out + q;
}

const REV_FIXED = Object.fromEntries(Object.entries(FIXED).map(([k, v]) => [v, k]));
const REV_NEW = Object.fromEntries(Object.entries(NEW_DOC).map(([k, v]) => [v, `vouchers/new/${k}`]));
const REPORT_SECTION: Record<string, string> = Object.fromEntries(REPORTS.map((r) => [r.name, r.section]));   // report → its internal area

export function fromUrl(url: string): string {
  const [raw] = split(url);
  const p = raw.replace(/\/+$/, "") || "/";
  if (p === "/") return "";
  if (REV_FIXED[p]) return REV_FIXED[p];
  if (REV_NEW[p]) return REV_NEW[p];
  let m: RegExpMatchArray | null;
  if ((m = p.match(/^\/(\w+)$/)) && SECTION_IDS.includes(m[1])) return m[1];
  if ((m = p.match(/^\/transactions\/([\w-]+)$/))) return `vouchers/v/${m[1]}`;
  if ((m = p.match(/^\/transactions\/([\w-]+)\/print$/))) return `vouchers/v/${m[1]}/print`;
  if ((m = p.match(/^\/accounting\/accounts\/([\w-]+)$/))) return `masters/ledgers/${m[1]}`;
  if ((m = p.match(/^\/inventory\/items\/([\w-]+)$/))) return `inventory/items/${m[1]}`;
  if ((m = p.match(/^\/(sales\/customers|purchases\/vendors)\/([\w-]+)\/statement$/))) return `parties/${m[1].split("/")[1]}/${m[2]}/statement`;
  if ((m = p.match(/^\/sales\/(quote|proforma|challan)s\/([\w-]+)(\/print)?$/))) return `orders/${m[1]}/${m[2]}${m[3] ?? ""}`;
  if ((m = p.match(/^\/(sales|purchases)\/orders\/([\w-]+)\/print$/))) return `orders/${m[1] === "sales" ? "sales" : "purchase"}/${m[2]}/print`;
  if ((m = p.match(/^\/(sales\/customers|purchases\/vendors)\/([\w-]+)$/))) return `parties/${m[1].split("/")[1]}/${m[2]}`;
  if ((m = p.match(/^\/(sales|purchases)\/(orders|recurring)\/([\w-]+)$/))) return `${m[2]}/${m[1] === "sales" ? "sales" : "purchase"}/${m[3]}`;
  if ((m = p.match(/^\/(\w+)\/reports\/([\w-]+)$/))) { const name = m[2].replace(/-/g, "_"); return `${REPORT_SECTION[name] ?? m[1]}/r/${name}`; }
  if ((m = p.match(/^\/reports\/([\w-]+)$/))) { const name = m[1].replace(/-/g, "_"); return `${REPORT_SECTION[name] ?? "reports"}/r/${name}`; }
  return p.slice(1);
}
